if (!customElements.get('incubalia-product-layers')) {
  class IncubaliaProductLayers extends HTMLElement {
    constructor() {
      super();
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-layer-button]'));
      this.panels = Array.from(this.querySelectorAll('[data-layer-panel]'));
      this.connectors = Array.from(this.querySelectorAll('[data-layer-connector]'));
      this.visual = this.querySelector('[data-visual]');
      this.detailsInner = this.querySelector('[data-details-inner]');
      this.imageWrapper = this.querySelector('.product-layers__image-wrapper');
      this.image = this.querySelector('.product-layers__image');
      this.desktopMediaQuery = window.matchMedia('(min-width: 990px)');
      this.activeIndex = Math.max(
        this.buttons.findIndex((button) => button.classList.contains('is-active')),
        0
      );

      if (!this.buttons.length || !this.panels.length || !this.visual || !this.detailsInner || !this.imageWrapper) {
        return;
      }

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.setActive(index));
        button.addEventListener('focus', () => this.setActive(index));
        button.addEventListener('mouseenter', () => {
          if (this.desktopMediaQuery.matches) {
            this.setActive(index);
          }
        });
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncLayout());
        this.resizeObserver.observe(this.visual);
        this.panels.forEach((panel) => this.resizeObserver.observe(panel));
      }

      if (typeof this.desktopMediaQuery.addEventListener === 'function') {
        this.desktopMediaQuery.addEventListener('change', this.onResize);
      } else if (typeof this.desktopMediaQuery.addListener === 'function') {
        this.desktopMediaQuery.addListener(this.onResize);
      }

      window.addEventListener('resize', this.onResize);

      if (this.image && !this.image.complete) {
        this.image.addEventListener('load', this.onResize);
      }

      this.setActive(this.activeIndex);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);

      if (this.image) {
        this.image.removeEventListener('load', this.onResize);
      }

      if (this.desktopMediaQuery) {
        if (typeof this.desktopMediaQuery.removeEventListener === 'function') {
          this.desktopMediaQuery.removeEventListener('change', this.onResize);
        } else if (typeof this.desktopMediaQuery.removeListener === 'function') {
          this.desktopMediaQuery.removeListener(this.onResize);
        }
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    onResize() {
      this.syncLayout();
    }

    setActive(index) {
      this.activeIndex = index;

      this.buttons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.panels.forEach((panel, panelIndex) => {
        panel.classList.toggle('is-active', panelIndex === index);
      });

      this.connectors.forEach((connector, connectorIndex) => {
        connector.classList.toggle('is-active', connectorIndex === index);
      });

      window.requestAnimationFrame(() => this.syncLayout());
    }

    syncLayout() {
      this.syncConnectors();

      if (!this.desktopMediaQuery.matches) {
        this.detailsInner.style.minHeight = '';
        this.style.removeProperty('--product-layers-panel-top');
        return;
      }

      const activeButton = this.buttons[this.activeIndex];
      const activePanel = this.panels[this.activeIndex];

      if (!activeButton || !activePanel) {
        return;
      }

      const visualHeight = this.visual.offsetHeight;
      const panelHeight = activePanel.offsetHeight;
      const minHeight = Math.max(visualHeight, panelHeight);
      const visualRect = this.visual.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const markerCenter = buttonRect.top - visualRect.top + buttonRect.height / 2;
      const maxTop = Math.max(minHeight - panelHeight, 0);
      const panelTop = Math.min(Math.max(markerCenter - panelHeight / 2, 0), maxTop);

      this.detailsInner.style.minHeight = `${minHeight}px`;
      this.style.setProperty('--product-layers-panel-top', `${panelTop}px`);
    }

    syncConnectors() {
      if (!this.connectors.length || !this.visual || !this.imageWrapper) {
        return;
      }

      if (!this.desktopMediaQuery.matches) {
        this.connectors.forEach((connector) => {
          connector.style.removeProperty('left');
          connector.style.removeProperty('top');
          connector.style.removeProperty('width');
        });
        return;
      }

      const visualRect = this.visual.getBoundingClientRect();
      const imageWrapperRect = this.imageWrapper.getBoundingClientRect();
      const wrapperOffsetLeft = imageWrapperRect.left - visualRect.left;
      const connectorGap = 16;

      this.connectors.forEach((connector) => {
        const x = parseFloat(connector.dataset.x || '50');
        const y = parseFloat(connector.dataset.y || '50');
        const left = (imageWrapperRect.width * x) / 100 + connectorGap;
        const top = (imageWrapperRect.height * y) / 100;
        const width = Math.max(visualRect.width - wrapperOffsetLeft - left, 0);

        connector.style.left = `${left}px`;
        connector.style.top = `${top}px`;
        connector.style.width = `${width}px`;
      });
    }
  }

  customElements.define('incubalia-product-layers', IncubaliaProductLayers);
}
